import { EntityType } from '../../../types.generated';
import {
    MANAGE_PLATFORM_PRIVILEGE_MAP,
    ENTITY_TYPE_PRIVILEGE_MAP,
    ENTITY_TYPE_NAME_MAP,
    SCHEMA_COL_ACTIONS,
    EntityMenuAction,
    ENTITY_ACTIONS,
    IngestionPrivileges,
    SecretPrivileges,
    SECRETS_ENTITY_TYPE,
    OwnershipTypePrivileges,
    PRIVILEGE_NAMES,
    OWNERSHIP_TYPE_PRIVILEGE_STRINGS,
} from './rbacConstantsService';

const {
    TAGS,
    GLOSSARY_TERMS,
    DOMAINS,
    DATA_PRODUCTS,
    OWNERS,
    DOC_LINKS,
    DOCS,
    UPDATE,
    REMOVE,
    EDIT,
    VIEW,
    LINEAGE,
    DELETE,
    EXPORT,
    UPDATE_GOV_STATUS,
    UPDATE_GOV_FLAG_STATUS,
    EDIT_READONLY,
    EDIT_NAME,
} = ENTITY_ACTIONS;

export type SidebarPrivilegeAction =
    | typeof TAGS
    | typeof GLOSSARY_TERMS
    | typeof DOMAINS
    | typeof DATA_PRODUCTS
    | typeof OWNERS
    | typeof DOC_LINKS
    | typeof DOCS;

export type EntityActionParams = {
    action?: string;
    entityType: EntityType;
    privilegeName: string;
    grantedPrivileges: string[];
    platformPrivileges?: Record<string, unknown> | null;
};

export enum CustomEntityType {
    GLOSSARY = 'GLOSSARY',
}

/**
 * Returns the platform "manage all" privilege for an entity type (e.g. 'manageDatasets' for Dataset).
 * If the user has this privilege, they bypass granular sidebar checks and can manage all sidebar actions.
 */
export function isPlatformManageEntityPrivilege(
    entityType: EntityType,
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    const bypassKey = MANAGE_PLATFORM_PRIVILEGE_MAP[entityType];
    if (bypassKey && platformPrivileges && platformPrivileges[bypassKey] === true) {
        return true;
    }
    return false;
}

const {
    TAGS: COL_TAGS,
    GLOSSARY_TERMS: COL_GLOSSARY_TERMS,
    DESCRIPTION: COL_DESCRIPTION,
    GOVERNANCE_FLAGS: COL_GOVERNANCE_FLAGS,
    DOMAINS: COL_DOMAINS,
} = SCHEMA_COL_ACTIONS;

export type SchemaColAction =
    | typeof COL_TAGS
    | typeof COL_GLOSSARY_TERMS
    | typeof COL_DESCRIPTION
    | typeof COL_GOVERNANCE_FLAGS
    | typeof COL_DOMAINS;

/**
 * Returns the privilege string for schema/column-level edits (PoliciesConfig: EDIT_<ENTITY>_COL_<ACTION>).
 * Only Dataset has schema column privileges in the backend.
 */
export function getSchemaColPrivilege(entityType: EntityType, action: SchemaColAction): string {
    const name = ENTITY_TYPE_NAME_MAP[entityType];
    return name ? `EDIT_${name}_COL_${action}` : '';
}

/**
 * Whether the user can perform a schema column action (edit col tags, terms, or description).
 * Bypass if platform manage (e.g. manageDatasets); else require EDIT_DATASET_COL_* from getGrantedPrivileges.
 */
export function canPerformSchemaColAction(
    entityType: EntityType,
    action: SchemaColAction,
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (isPlatformManageEntityPrivilege(entityType, platformPrivileges)) {
        return true;
    }
    const requiredPrivilege = getSchemaColPrivilege(entityType, action);
    return requiredPrivilege ? grantedPrivileges.includes(requiredPrivilege) : false;
}

/**
 * Returns the privilege string for an entity type and action.
 * Used when the user does not have the platform-level manage bypass.
 */
export function getEntityPrivilege(action: string, entityType: EntityType, privilegeName: string): string {
    const name = ENTITY_TYPE_NAME_MAP[entityType];
    return name ? `${action}_${name}_${privilegeName}` : '';
}

/**
 * Whether the user can add/edit structured properties. Bypass if platform manage; else require UPDATE_*_STRUCTURED_PROPERTIES.
 */
export function canPerformStructuredPropertiesUpdate(
    entityType: EntityType,
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (isPlatformManageEntityPrivilege(entityType, platformPrivileges)) {
        return true;
    }
    const required = getEntityPrivilege(UPDATE, entityType, PRIVILEGE_NAMES.STRUCTURED_PROPERTIES);
    return required ? grantedPrivileges.includes(required) : false;
}

/**
 * Whether the user can remove structured properties. Bypass if platform manage; else require REMOVE_*_STRUCTURED_PROPERTIES.
 */
export function canPerformStructuredPropertiesRemove(
    entityType: EntityType,
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (isPlatformManageEntityPrivilege(entityType, platformPrivileges)) {
        return true;
    }
    const required = getEntityPrivilege(REMOVE, entityType, PRIVILEGE_NAMES.STRUCTURED_PROPERTIES);
    return required ? grantedPrivileges.includes(required) : false;
}

/**
 * Whether the user can add/update custom properties. Bypass if platform manage; else require UPDATE_*_CUSTOM_PROPERTIES.
 */
export function canPerformCustomPropertiesUpdate(
    entityType: EntityType,
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (isPlatformManageEntityPrivilege(entityType, platformPrivileges)) {
        return true;
    }

    const required = getEntityPrivilege(UPDATE, entityType, PRIVILEGE_NAMES.CUSTOM_PROPERTIES);
    return required ? grantedPrivileges.includes(required) : false;
}

/**
 * Whether the user can remove custom properties. Bypass if platform manage; else require REMOVE_*_CUSTOM_PROPERTIES.
 */
export function canPerformCustomPropertiesRemove(
    entityType: EntityType,
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (isPlatformManageEntityPrivilege(entityType, platformPrivileges)) {
        return true;
    }
    const required = getEntityPrivilege(REMOVE, entityType, PRIVILEGE_NAMES.CUSTOM_PROPERTIES);
    return required ? grantedPrivileges.includes(required) : false;
}

/** Dataset query privileges (CREATE/UPDATE/DELETE_DATASET_QUERY). Bypass if manageDatasetsQuery. */
const DATASET_QUERY_PRIVILEGES = {
    CREATE: 'CREATE_DATASET_QUERY',
    UPDATE: 'UPDATE_DATASET_QUERY',
    DELETE: 'DELETE_DATASET_QUERY',
} as const;

export function canCreateDatasetQuery(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (isPlatformManageEntityPrivilege(EntityType.Query, platformPrivileges)) {
        return true;
    }
    return grantedPrivileges.includes(DATASET_QUERY_PRIVILEGES.CREATE);
}

export function canUpdateDatasetQuery(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (isPlatformManageEntityPrivilege(EntityType.Query, platformPrivileges)) {
        return true;
    }
    return grantedPrivileges.includes(DATASET_QUERY_PRIVILEGES.UPDATE);
}

export function canDeleteDatasetQuery(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (isPlatformManageEntityPrivilege(EntityType.Query, platformPrivileges)) {
        return true;
    }
    return grantedPrivileges.includes(DATASET_QUERY_PRIVILEGES.DELETE);
}

/** Assertion privileges (DELETE_ASSERTION). Bypass if manageAssertions platform privilege. */
const ASSERTION_PRIVILEGES = {
    DELETE: 'DELETE_ASSERTION',
} as const;

export function canDeleteAssertion(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (platformPrivileges?.manageAssertions === true) {
        return true;
    }
    return grantedPrivileges.includes(ASSERTION_PRIVILEGES.DELETE);
}

/** Data Product privileges (CREATE/UPDATE/DELETE/UPDATE_GOV_STATUS/BULK_IMPORT). Bypass if manageDataProducts. */
const DATA_PRODUCT_PRIVILEGES = {
    CREATE: 'CREATE_DATA_PRODUCT',
    UPDATE: 'UPDATE_DATA_PRODUCT',
    DELETE: 'DELETE_DATA_PRODUCT',
    UPDATE_GOV_STATUS: 'UPDATE_DATA_PRODUCT_GOV_STATUS',
    BULK_IMPORT: 'BULK_DATA_PRODUCT_IMPORT',
} as const;

export function canCreateDataProduct(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (isPlatformManageEntityPrivilege(EntityType.DataProduct, platformPrivileges)) {
        return true;
    }
    return grantedPrivileges.includes(DATA_PRODUCT_PRIVILEGES.CREATE);
}

export function canUpdateDataProduct(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (isPlatformManageEntityPrivilege(EntityType.DataProduct, platformPrivileges)) {
        return true;
    }
    return grantedPrivileges.includes(DATA_PRODUCT_PRIVILEGES.UPDATE);
}

export function canBulkImportDataProduct(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (isPlatformManageEntityPrivilege(EntityType.DataProduct, platformPrivileges)) {
        return true;
    }
    return grantedPrivileges.includes(DATA_PRODUCT_PRIVILEGES.BULK_IMPORT);
}

export type { EntityMenuAction } from './rbacConstantsService';

/**
 * Whether the user can perform an entity profile menu action for the given entity type.
 * Bypass if user has platform manage privilege for that entity; else require the mapped privilege from getGrantedPrivileges.
 * If no privilege is mapped for (entityType, action), returns true (no RBAC gate).
 */
export function canPerformEntityMenuAction(
    entityType: EntityType,
    action: EntityMenuAction,
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    const required = ENTITY_TYPE_PRIVILEGE_MAP[entityType]?.[action];
    if (!required) {
        return true;
    }
    if (isPlatformManageEntityPrivilege(entityType, platformPrivileges)) {
        return true;
    }
    return grantedPrivileges.includes(required);
}

export function canDeleteDataProduct(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    return canPerformEntityMenuAction(EntityType.DataProduct, DELETE, grantedPrivileges, platformPrivileges);
}

export function canExportDataProduct(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    return canPerformEntityMenuAction(EntityType.DataProduct, EXPORT, grantedPrivileges, platformPrivileges);
}

export function canUpdateDataProductGovStatus(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    return canPerformEntityMenuAction(EntityType.DataProduct, UPDATE_GOV_STATUS, grantedPrivileges, platformPrivileges);
}

/** Entity types that support editable display name in the header (glossary term/node, domain, data product). */
const NAME_EDITABLE_ENTITY_TYPES: EntityType[] = [
    EntityType.GlossaryTerm,
    EntityType.GlossaryNode,
    EntityType.Domain,
    EntityType.DataProduct,
];

/**
 * Whether the user can edit the entity display name for the given entity type.
 * Uses EDIT_NAME privilege map (UPDATE_GLOSSARY_TERM_NAME, UPDATE_GLOSSARY_NODE_NAME, UPDATE_DOMAIN_NAME, UPDATE_DATA_PRODUCT_NAME)
 * with platform manage bypass. Returns false for entity types that do not support name editing.
 */
export function canEditEntityName(
    entityType: EntityType,
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (!NAME_EDITABLE_ENTITY_TYPES.includes(entityType)) {
        return false;
    }
    return canPerformEntityMenuAction(entityType, EDIT_NAME, grantedPrivileges, platformPrivileges);
}

/**
 * Whether the user can perform a related-terms action on a Glossary Term or Glossary Node.
 * Bypass if platform manage (manageGlossaryTerms / manageGlossaryNodes); else require the mapped privilege.
 * If no privilege is mapped for (parentEntityType, action), returns true (no RBAC gate).
 */
export function canPerformRelatedTermAction(
    parentEntityType: EntityType,
    action: EntityMenuAction,
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    return canPerformEntityMenuAction(parentEntityType, action, grantedPrivileges, platformPrivileges);
}

/** @deprecated Use canPerformEntityMenuAction(entityType, DELETE, ...) for delete. Kept for compatibility. */
export function canDeleteDataset(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    return canPerformEntityMenuAction(EntityType.Dataset, DELETE, grantedPrivileges, platformPrivileges);
}

/** @deprecated Use canPerformEntityMenuAction(entityType, UPDATE_GOV_STATUS, ...). */
export function canUpdateDatasetGovStatus(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    return canPerformEntityMenuAction(EntityType.Dataset, UPDATE_GOV_STATUS, grantedPrivileges, platformPrivileges);
}

/** @deprecated Use canPerformEntityMenuAction(entityType, UPDATE_GOV_FLAG_STATUS, ...). */
export function canUpdateDatasetGovFlagStatus(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    return canPerformEntityMenuAction(
        EntityType.Dataset,
        UPDATE_GOV_FLAG_STATUS,
        grantedPrivileges,
        platformPrivileges,
    );
}

/** @deprecated Use canPerformEntityMenuAction(entityType, EDIT_READONLY, ...). */
export function canEditDatasetReadOnly(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    return canPerformEntityMenuAction(EntityType.Dataset, EDIT_READONLY, grantedPrivileges, platformPrivileges);
}

/**
 * -------------------------
 * Generic Entity Actions
 * -------------------------
 */

/**
 * Returns whether the user can perform a sidebar action on the given entity type.
 * 1. If the user has the platform manage privilege from getMe (e.g. manageDatasets === true), bypass → allow.
 * 2. If not (e.g. manageDatasets: false, viewDataset: true), require the specific RBAC privilege from getGrantedPrivileges (e.g. UPDATE_DATASET_TAGS).
 * @param platformPrivileges From getMe (UserContext), called on every page load.
 */
export function canPerformEntityAction(entityActionParams: EntityActionParams): boolean {
    const { action = UPDATE, entityType, privilegeName, grantedPrivileges, platformPrivileges } = entityActionParams;
    if (isPlatformManageEntityPrivilege(entityType, platformPrivileges)) {
        return true;
    }
    const requiredPrivilege = getEntityPrivilege(action, entityType, privilegeName);
    return requiredPrivilege ? grantedPrivileges.includes(requiredPrivilege) : false;
}

export function canViewChangeLog(
    entityType: EntityType,
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    if (isPlatformManageEntityPrivilege(entityType, platformPrivileges)) {
        return true;
    }

    const requiredPrivilege = getEntityPrivilege(VIEW, entityType, PRIVILEGE_NAMES.AUDIT);
    return requiredPrivilege ? grantedPrivileges.includes(requiredPrivilege) : false;
}

// RBAC for Data Product
export function canUpdateDataProductOwners(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    return canPerformEntityAction({
        entityType: EntityType.DataProduct,
        privilegeName: OWNERS,
        grantedPrivileges,
        platformPrivileges,
    });
}

// RBAC for Data Product Tags
export function canUpdateDataProductTags(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    return canPerformEntityAction({
        entityType: EntityType.DataProduct,
        privilegeName: TAGS,
        grantedPrivileges,
        platformPrivileges,
    });
}

// RBAC for Data Product Glossary Terms
export function canUpdateDataProductGlossaryTerms(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    return canPerformEntityAction({
        entityType: EntityType.DataProduct,
        privilegeName: GLOSSARY_TERMS,
        grantedPrivileges,
        platformPrivileges,
    });
}

// RBAC for Data Product Domains
export function canUpdateDataProductDomains(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): boolean {
    return canPerformEntityAction({
        entityType: EntityType.DataProduct,
        privilegeName: DOMAINS,
        grantedPrivileges,
        platformPrivileges,
    });
}

// RBAC for Lineage
export function canEditLineage(entityActionParams: EntityActionParams): boolean {
    return canPerformEntityAction({
        action: EDIT,
        entityType: entityActionParams.entityType,
        privilegeName: LINEAGE,
        grantedPrivileges: entityActionParams.grantedPrivileges,
        platformPrivileges: entityActionParams.platformPrivileges,
    });
}

export function canManageGlossary(platformPrivileges?: Record<string, unknown> | null) {
    const manageGlossary = platformPrivileges?.manageGlossaryNodes || platformPrivileges?.manageGlossaryTerms || false;
    const viewGlossary = platformPrivileges?.viewGlossaryNode || platformPrivileges?.viewGlossaryTerm || false;

    return viewGlossary || manageGlossary || false;
}

/**
 * Transforms a permission map into a tooltip map.
 * Returns the unauthorized message if the permission is false, otherwise undefined.
 */
/**
 * Resolves the full set of ingestion privileges for the current user.
 *
 * Priority (mirrors backend PoliciesConfig.INGESTION_PRIVILEGES):
 *  1. platformPrivileges.manageIngestion → full access (bypass all fine-grained checks).
 *  2. Otherwise check each fine-grained privilege from getGrantedPrivileges.
 */
export function resolveIngestionPrivileges(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): IngestionPrivileges {
    if (platformPrivileges?.manageIngestion) {
        return { canView: true, canCreate: true, canUpdate: true, canDelete: true, canExecute: true, canCancel: true };
    }
    const ingestionMap = ENTITY_TYPE_PRIVILEGE_MAP[EntityType.IngestionSource];
    return {
        canView: !!platformPrivileges?.viewIngestion || grantedPrivileges.includes(ingestionMap?.VIEW ?? ''),
        canCreate: grantedPrivileges.includes(ingestionMap?.CREATE ?? ''),
        canUpdate: grantedPrivileges.includes(ingestionMap?.UPDATE ?? ''),
        canDelete: grantedPrivileges.includes(ingestionMap?.DELETE ?? ''),
        canExecute: grantedPrivileges.includes(ingestionMap?.EXECUTE ?? ''),
        canCancel: grantedPrivileges.includes(ingestionMap?.CANCEL ?? ''),
    };
}

/**
 * Resolves the full set of secret privileges for the current user.
 *
 * Priority (mirrors backend PoliciesConfig.SECRET_PRIVILEGES):
 *  1. platformPrivileges.manageSecrets → full access (view, create, delete).
 *  2. Fine-grained entity-level secret privileges (CREATE_SECRET, DELETE_SECRET, VIEW_SECRETS)
 *     require a separate getGrantedPrivileges call scoped to the dataHubSecret resource type,
 *     which is not yet wired through the current GrantedPrivilegesProvider flow.
 */
export function resolveSecretPrivileges(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): SecretPrivileges {
    if (platformPrivileges?.manageSecrets) {
        return { canView: true, canCreate: true, canDelete: true };
    }
    return {
        canView:
            !!platformPrivileges?.viewSecrets ||
            grantedPrivileges.includes(ENTITY_TYPE_PRIVILEGE_MAP[SECRETS_ENTITY_TYPE]?.VIEW ?? ''),
        canCreate: grantedPrivileges.includes(ENTITY_TYPE_PRIVILEGE_MAP[SECRETS_ENTITY_TYPE]?.CREATE ?? ''),
        canDelete: grantedPrivileges.includes(ENTITY_TYPE_PRIVILEGE_MAP[SECRETS_ENTITY_TYPE]?.DELETE ?? ''),
    };
}

// -------------------------
// Ownership Types
// -------------------------

/**
 * Resolves the full set of ownership type privileges for the current user.
 *
 * Priority (mirrors backend PoliciesConfig.OWNERSHIP_TYPE_PRIVILEGES):
 *  1. platformPrivileges.manageOwnershipTypes → full access (bypass all fine-grained checks).
 *  2. platformPrivileges.viewOwnershipTypes   → view-only access.
 *  3. Otherwise check each fine-grained privilege from getGrantedPrivileges.
 */
export function resolveOwnershipTypePrivileges(
    grantedPrivileges: string[],
    platformPrivileges?: Record<string, unknown> | null,
): OwnershipTypePrivileges {
    if (platformPrivileges?.manageOwnershipTypes) {
        return { canView: true, canCreate: true, canUpdate: true, canDelete: true };
    }
    return {
        canView:
            !!platformPrivileges?.viewOwnershipTypes ||
            grantedPrivileges.includes(OWNERSHIP_TYPE_PRIVILEGE_STRINGS.VIEW),
        canCreate: grantedPrivileges.includes(OWNERSHIP_TYPE_PRIVILEGE_STRINGS.CREATE),
        canUpdate: grantedPrivileges.includes(OWNERSHIP_TYPE_PRIVILEGE_STRINGS.UPDATE),
        canDelete: grantedPrivileges.includes(OWNERSHIP_TYPE_PRIVILEGE_STRINGS.DELETE),
    };
}

export const getPermissionTooltips = (
    t: (key: string) => string,
    permissions: Record<string, boolean | undefined>,
): Record<string, string | undefined> => {
    const unauthorizedTooltip = t('UNAUTHORISED_TOOLTIP');

    return Object.entries(permissions).reduce((acc, [key, hasPermission]) => {
        // If the permission is false, set the tooltip to the unauthorized tooltip, otherwise set it to undefined
        acc[key] = !hasPermission ? unauthorizedTooltip : undefined;
        return acc;
    }, {} as Record<string, string | undefined>);
};
